import styled, { css } from 'styled-components';
import { textStyleCss } from 'src/theme/atlas';

export const DynamicLayoutRoot = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
  gap: 16px;
`;

export const TopCategoryRow = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  grid-template-rows: auto auto;
  column-gap: 14px;
  row-gap: 6px;
  width: 100%;
  align-items: center;
`;

export const TopCategorySwitchCell = styled.div`
  grid-row: 1;
  grid-column: 1;
  display: flex;
  align-items: center;
`;

export const TopCategoryTitle = styled.p`
  grid-row: 1;
  grid-column: 2;
  ${({ theme }) =>
    theme.isVaultVariant
      ? theme.newTypography.textStyleBodySemibold
      : textStyleCss(theme.atlas.typography.semantic.bodySmall)}
  ${({ theme }) => !theme.isVaultVariant && 'font-weight: 600;'}
  color: ${(p) =>
    p.theme.isVaultVariant
      ? p.theme.newPalette.neutral.N800
      : p.theme.atlas.semantic.color.type.default};
  margin: 0;
  min-width: 0;
  text-transform: uppercase;
  letter-spacing: 0.02em;
`;

export const TopCategoryDescription = styled.p`
  grid-row: 2;
  grid-column: 1 / -1;
  ${({ theme }) =>
    theme.isVaultVariant
      ? theme.newTypography.textStyleBodySmall
      : textStyleCss(theme.atlas.typography.semantic.caption)}
  color: ${(p) =>
    p.theme.isVaultVariant
      ? p.theme.newPalette.neutral.N600
      : p.theme.atlas.semantic.color.type.muted};
  margin: 0;
`;

export const QuestionFlowOuter = styled.div`
  display: flex;
  flex-direction: column;
  overflow: visible;
  background: ${(p) =>
    p.theme.isVaultVariant
      ? p.theme.newPalette.neutral.N0
      : p.theme.atlas.semantic.color.surface.default};
`;

export const QuestionFlowContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-top: 16px;
  padding: 0;
  min-height: 120px;
  flex: 1 1 auto;
  width: 100%;
  background: ${(p) =>
    p.theme.isVaultVariant
      ? p.theme.newPalette.neutral.N0
      : p.theme.atlas.semantic.color.surface.default};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const QuestionFlowColumn = styled.div<{ $backgroundColor?: string }>`
  padding: 20px;
  margin: 0;
  border: 1px solid
    ${(p) =>
      p.theme.isVaultVariant
        ? p.theme.newPalette.neutral.N300
        : p.theme.atlas.semantic.color.outline.fixed};
  border-radius: 8px;
  background-color: ${(p) =>
    p.$backgroundColor ||
    (p.theme.isVaultVariant
      ? p.theme.newPalette.neutral.N0
      : p.theme.atlas.semantic.color.surface.default)};
`;

export const SectionHeading = styled.h3`
  ${({ theme }) =>
    theme.isVaultVariant
      ? theme.newTypography.textStyleBodySemibold
      : textStyleCss(theme.atlas.typography.semantic.bodySmall)}
  ${({ theme }) => !theme.isVaultVariant && 'font-weight: 600;'}
  color: ${(p) =>
    p.theme.isVaultVariant
      ? p.theme.newPalette.neutral.N800
      : p.theme.atlas.semantic.color.type.default};
  margin: 0 0 8px 0;
`;

export const SectionDescription = styled.p`
  ${({ theme }) =>
    theme.isVaultVariant
      ? theme.newTypography.textStyleBodySmall
      : textStyleCss(theme.atlas.typography.semantic.caption)}
  color: ${(p) =>
    p.theme.isVaultVariant
      ? p.theme.newPalette.neutral.N600
      : p.theme.atlas.semantic.color.type.muted};
  margin: 0 0 16px 0;
`;

export const RadioStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const IntakeChannelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const IntakeChannelRowSubOption = styled(IntakeChannelRow)`
  padding-inline-start: 24px;
  box-sizing: border-box;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 6px;
    top: -8px;
    width: 10px;
    height: calc(50% + 8px);
    border-left: 1.5px solid currentColor;
    border-bottom: 1.5px solid currentColor;
  }
`;

export const IntakeChannelLabel = styled.div<{ isDisabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  ${({ theme }) =>
    theme.isVaultVariant
      ? theme.newTypography.textStyleBodySemibold
      : textStyleCss(theme.atlas.typography.semantic.bodySmall)}
  ${({ theme }) => !theme.isVaultVariant && 'font-weight: 600;'}
  color: ${(p) =>
    p.theme.isVaultVariant
      ? p.isDisabled
        ? p.theme.newPalette.neutral.N600
        : p.theme.newPalette.neutral.N800
      : p.isDisabled
        ? p.theme.atlas.semantic.color.type.disabled
        : p.theme.atlas.semantic.color.type.default};
`;

export const ValidationMessage = styled.p`
  ${({ theme }) =>
    theme.isVaultVariant
      ? theme.newTypography.textStyleBodySmall
      : textStyleCss(theme.atlas.typography.semantic.caption)}
  color: ${(p) =>
    p.theme.isVaultVariant
      ? p.theme.newPalette.red.R300
      : p.theme.atlas.semantic.color.status.error.default};
  margin: 8px 0 0 0;
`;

export const TabRow = styled.div`
  display: flex;
  flex-shrink: 0;
  width: 100%;
  margin: 0;
  padding: 0;
  background: transparent;
`;

export const IconTooltipTriggerButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  cursor: help;
  border-radius: 4px;
  color: inherit;
  font: inherit;

  &:focus-visible {
    outline: 2px solid
      ${(p) =>
        p.theme.isVaultVariant
          ? p.theme.newPalette.turquoise.T400
          : p.theme.atlas.semantic.color.ui.focusRing};
    outline-offset: 2px;
  }
`;

type TabSegment = 'first' | 'last';

export const TabButtonLabelRow = styled.span`
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-width: 0;
  gap: 8px;
`;

export const TabButtonLabelText = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  text-align: center;
`;

export const TabButtonTrailing = styled.span`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 6px;
`;

export const TabButton = styled.button<{
  $active: boolean;
  $segment: TabSegment;
  $locked?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  cursor: pointer;
  padding: 12px 16px;
  ${({ theme }) =>
    theme.isVaultVariant
      ? theme.newTypography.textStyleBody
      : textStyleCss(theme.atlas.typography.semantic.bodySmall)}
  color: ${(p) =>
    p.theme.isVaultVariant
      ? p.theme.newPalette.neutral.N800
      : p.theme.atlas.semantic.color.type.default};
  margin: 0;
  box-sizing: border-box;
  border-bottom: none;

  ${(p) => {
    const { T100, T400 } = p.theme.newPalette.turquoise;
    const { N0, N300 } = p.theme.newPalette.neutral;
    const activeBackground = p.theme.isVaultVariant
      ? T100
      : p.theme.atlas.semantic.color.selection.primary.default;
    const activeBorder = p.theme.isVaultVariant
      ? T400
      : p.theme.atlas.semantic.color.selection.primary.default;
    const activeColor = p.theme.isVaultVariant
      ? p.theme.newPalette.neutral.N800
      : p.theme.atlas.semantic.color.selection.primary.onSelected;
    const inactiveBackground = p.theme.isVaultVariant
      ? N0
      : p.theme.atlas.semantic.color.surface.default;
    const inactiveBorder = p.theme.isVaultVariant
      ? N300
      : p.theme.atlas.semantic.color.outline.fixed;
    if (p.$active) {
      return css`
        background: ${activeBackground};
        border-style: solid;
        border-color: ${activeBorder};
        border-width: 1px;
        color: ${activeColor};
      `;
    }
    return css`
      background: ${inactiveBackground};
      border-style: solid;
      border-color: ${inactiveBorder};
      border-width: 1px;
      ${p.$segment === 'first'
        ? css`
            border-right-width: 0;
          `
        : css`
            border-left-width: 0;
          `}
    `;
  }}

  ${(p) =>
    p.$locked &&
    css`
      cursor: not-allowed;
      background: ${p.theme.isVaultVariant
        ? p.theme.newPalette.neutral.N200
        : p.theme.atlas.semantic.color.action.primary.disabled};
      border-color: ${p.theme.isVaultVariant
        ? p.theme.newPalette.neutral.N300
        : p.theme.atlas.semantic.color.outline.disabled};
      color: ${p.theme.isVaultVariant
        ? p.theme.newPalette.neutral.N600
        : p.theme.atlas.semantic.color.type.disabled};
    `}

  &:disabled {
    cursor: not-allowed;
    background: ${(p) =>
      p.theme.isVaultVariant
        ? p.theme.newPalette.neutral.N200
        : p.theme.atlas.semantic.color.action.primary.disabled};
    border-color: ${(p) =>
      p.theme.isVaultVariant
        ? p.theme.newPalette.neutral.N300
        : p.theme.atlas.semantic.color.outline.disabled};
    opacity: 1;
  }
`;

export const CustomFlowNotice = styled.div`
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const CustomFlowNoticeHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

export const CustomFlowNoticeTitle = styled.span`
  ${({ theme }) =>
    theme.isVaultVariant
      ? theme.newTypography.textStyleBodySemibold
      : textStyleCss(theme.atlas.typography.semantic.bodySmall)}
  ${({ theme }) => !theme.isVaultVariant && 'font-weight: 600;'}
  color: ${(p) =>
    p.theme.isVaultVariant
      ? p.theme.newPalette.neutral.N800
      : p.theme.atlas.semantic.color.type.default};
`;

export const CustomFlowNoticeBody = styled.div`
  ${({ theme }) =>
    theme.isVaultVariant
      ? theme.newTypography.textStyleBodySmall
      : textStyleCss(theme.atlas.typography.semantic.caption)}
  color: ${(p) =>
    p.theme.isVaultVariant
      ? p.theme.newPalette.neutral.N600
      : p.theme.atlas.semantic.color.type.muted};
  margin: 0;
  padding-left: 28px;
`;
